import { Stack } from "@mui/material";
import {
  PaymentStatus,
  STATUSES_ALLOWING_PAYMENT,
} from "Components/AnonymousCustomer/Payment/PaymentDetail/PaymentStatus";
import {
  BottomSection,
  MainSection,
} from "Components/AnonymousCustomer/Payment/SectionLayout";
import { TermsAgreementText } from "Components/AnonymousCustomer/Payment/TermsAgreementText";
import type { PaymentStep } from "Components/AnonymousCustomer/Payment/config";
import { MerchantHeader } from "Components/Customer/Payment/MerchantHeader";
import { PaymentAmountCard } from "Components/Customer/Payment/PaymentAmountCard";
import { TipSection } from "Components/Customer/Payment/PaymentForms/TipSection";
import { computeTipAmount } from "Components/Customer/Payment/PaymentForms/tipUtils";
import type { TipMode } from "Components/Shared/TipSelector/types";
import {
  type TransactionDetailDto,
  TransactionStatus,
} from "Infrastructure/Api/Api";
import { useState } from "react";

type Props = {
  transactionDetail: TransactionDetailDto;
  transactionPublicID: string;
  onNextStepRequested: (nextStep?: PaymentStep) => void;
  campaignCode?: string;
  affiliateCode?: string;
};

export const AnonymousPaymentDetailContent = ({
  transactionDetail: {
    tipAmount,
    payPointTipsEnabled,
    transactionStatus,
    amount,
    creditorName,
    payPointPublicID,
    merchantIdentityUserPublicID,
    merchantProfilePicture,
    currencyCode,
    expirationDate,
    failureReason,
  },
  transactionPublicID,
  onNextStepRequested,
  campaignCode,
  affiliateCode,
}: Props) => {
  const [tipMode, setTipMode] = useState<TipMode>(
    tipAmount === 0 ? "none" : "custom",
  );
  const [customTipAmount, setCustomTipAmount] = useState<number | undefined>(
    tipAmount > 0 ? tipAmount : undefined,
  );

  const handleTipModeChange = (mode: TipMode) => {
    setTipMode(mode);
    if (mode !== "custom") {
      setCustomTipAmount(undefined);
    }
  };

  // An expired transaction may still be Created until the cancellation job picks it up —
  // treat it as not payable right away.
  const isExpired = !!expirationDate && new Date(expirationDate) <= new Date();
  const isTipEditingEnabled =
    payPointTipsEnabled &&
    transactionStatus === TransactionStatus.Created &&
    !isExpired;
  const isPaymentAllowed =
    STATUSES_ALLOWING_PAYMENT.includes(transactionStatus) && !isExpired;

  const baseAmount = amount - tipAmount;
  const pendingTipAmount = isTipEditingEnabled
    ? computeTipAmount(tipMode, customTipAmount, baseAmount)
    : undefined;

  return (
    <Stack gap={7} flexGrow={1}>
      <MainSection>
        <MerchantHeader
          creditorName={creditorName}
          payPointPublicID={payPointPublicID}
          merchantIdentityUserPublicID={merchantIdentityUserPublicID}
          merchantProfilePicture={merchantProfilePicture}
        />
        <Stack gap={6}>
          <PaymentAmountCard
            billAmount={baseAmount}
            tipAmount={pendingTipAmount ?? tipAmount}
            currencyCode={currencyCode}
            arePayPointTipsEnabled={payPointTipsEnabled}
          />
          {isTipEditingEnabled && (
            <TipSection
              tipMode={tipMode}
              currencyCode={currencyCode}
              customTipAmount={customTipAmount}
              onTipModeChange={handleTipModeChange}
              onCustomTipAmountChange={setCustomTipAmount}
            />
          )}
        </Stack>
        {isPaymentAllowed && <TermsAgreementText />}
      </MainSection>
      <BottomSection>
        <PaymentStatus
          transactionPublicID={transactionPublicID}
          onNextStepRequested={onNextStepRequested}
          transactionStatus={transactionStatus}
          campaignCode={campaignCode}
          affiliateCode={affiliateCode}
          failureReason={failureReason}
          pendingTipAmount={pendingTipAmount}
          isExpired={isExpired}
        />
      </BottomSection>
    </Stack>
  );
};
